import { useEffect, useState, useCallback } from 'preact/hooks';
import posService from "../service/posService";
import Spinner from './Spinner';
import constants from "../constants";
import { h } from 'preact';
import locationService from "../service/locationService";
import SquareIcon from "./icon/SquareIcon";
import ConfigIcon from "./icon/ConfigIcon";
import SquareLargeIcon from "./icon/SquareLargeIcon";
import { BarsOutlined, SettingOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';

const source = constants.IMPORT_SOURCE_SQUARE;
const emptyMenu = [posService.mapMenu({}, source, '', '')];

export function SquarePosMenuImport(props) {
  const [importing, setImporting] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [assignMode, setAssignMode] = useState(false);
  const [activePosLocation, setActivePosLocation] = useState(null);
  const [activeMhmLocation, setActiveMhmLocation] = useState(null);
  const [posLocations, setPosLocations] = useState([]);
  const [mhmLocations, setMhmLocations] = useState([]);
  const [selPosLocation, setSelPosLocation] = useState(null);
  const [pendingAuthorization, setPendingAuthorization] = useState(false);

  useEffect(() => {
    setImporting(true);
    posService.getSquareRestaurants()
    .then(resp => {
      setRestaurant(resp.data[0]);
      setImporting(false);
    })
    .catch(err => {
      setImporting(false);
    });
  }, []);

  useEffect(() => {
    setMhmLocations(window.data.locations);
  }, []);

  useEffect(() => {
    window.data.locations = mhmLocations;
  }, [mhmLocations]);

  useEffect(() => {
    if (!mhmLocations?.length) return;
    setActiveMhmLocation(mhmLocations.find(it => it.internalId === window.data.design.locationId));
  }, [mhmLocations]);

  useEffect(() => {
    if (!restaurant?.id) return;
    posService.getSquareLocations(restaurant.id).then(resp => {
      const locations = resp.data;
      locations.sort((a, b) => b.createdDate - a.createdDate);
      setPosLocations(locations);
    });
  }, [restaurant]);

  useEffect(() => {
    if (!restaurant) return;
    setPendingAuthorization(!restaurant.scopes || restaurant.scopes.indexOf('INVENTORY_READ') < 0);
  }, [restaurant]);

  useEffect(() => {
    if (!restaurant?.id || assignMode) return;
    if (!activePosLocation) {
      props.onSuccess(emptyMenu, "Square");
    } else {
      fetchMenus(restaurant.merchantId, activePosLocation);
    }
  }, [activePosLocation, restaurant, assignMode]);

  const triggerImportMenus = useCallback(() => {
    if (!posLocations?.length || !activeMhmLocation || !restaurant) return;

    if (posLocations.length === 1) {
      if (activeMhmLocation.squareLocationId === posLocations[0].dbId) {
        if (activePosLocation?.id !== posLocations[0].id) {
          setActivePosLocation(posLocations[0]);
        }
      } else {
        setActivePosLocation(null);
      }
    } else if (!!activeMhmLocation && !!activeMhmLocation.squareLocationId) {
      const found = posLocations.find(it => it.dbId === activeMhmLocation.squareLocationId);
      if (found) {
        if (activePosLocation?.id !== found.id) {
          setActivePosLocation(found);
        }
      }
    }
  }, [posLocations, activeMhmLocation, restaurant, activePosLocation]);

  useEffect(() => triggerImportMenus, [triggerImportMenus]);

  useEffect(() => {
    if (!posLocations?.length || !mhmLocations?.length) return;

    let posLocFound = false;
    const updatedPosLocations = posLocations.map(squareLoc => {
      const foundMhmLocation = mhmLocations.find(mhm => mhm.squareLocationId === squareLoc.dbId);
      if (foundMhmLocation) {
        if (squareLoc.mhmLocationId !== foundMhmLocation.internalId || squareLoc.mhmLocation !== foundMhmLocation.name) {
          posLocFound = true;
          return {
            ...squareLoc,
            mhmLocationId: foundMhmLocation.internalId,
            mhmLocation: foundMhmLocation.name,
          };
        }
      }
      return squareLoc;
    });

    let mhmLocFound = false;
    const updatedMhmLocations = mhmLocations.map(mhmLoc => {
      const squareLoc = posLocations.find(square => square.dbId === mhmLoc.squareLocationId);
      if (squareLoc) {
        const name = squareLoc.locationName || squareLoc.name;
        if (mhmLoc.posLocation !== name || mhmLoc.squareLocationId !== squareLoc.dbId) {
          mhmLocFound = true;
          return {
            ...mhmLoc,
            posLocation: name,
            squareLocationId: squareLoc.dbId,
          };
        }
      }
      return mhmLoc;
    });

    if (mhmLocFound) {
      setMhmLocations(updatedMhmLocations);
    }
    if (posLocFound) {
      setPosLocations(updatedPosLocations);
    }
  }, [posLocations, mhmLocations]);

  function fetchMenus(restaurantId, posLocation) {
    if (!posLocation || !posLocation.dbId || !restaurantId) return;
    setImporting(true);

    try {
      posService.getSquareMenus(restaurantId).then(resp => {
        if (resp.data) {
          let menu = resp.data;
          const mapped = [posService.mapMenu(menu, source, posLocation.name, posLocation.id)];
          props.onSuccess(mapped, "Square");
        }
        setImporting(false);
      })
    } catch (e) {
      console.error(e);
      setImporting(false);
    }
  }

  function doneAssign() {
    const data = [];
    let activePosLoc = null;
    posLocations.forEach(it => {
      if (it.mhmLocationId) {
        data.push({
          id: it.mhmLocationId,
          posLocationId: it.dbId
        });

        if (it.mhmLocationId === activeMhmLocation.internalId) {
          activePosLoc = it;
        }
      }
    });

    mhmLocations.forEach(loc => {
      const mapped = data.find(it => it.id === loc.internalId);
      if (!mapped) {
        data.push({
          id: loc.internalId,
          posLocationId: ''
        });
      }
    });

    locationService.assignSquareLocation(data);
    setMhmLocations([...mhmLocations]);
    setActivePosLocation(activePosLoc);

    setAssignMode(false);
  }

  function onLocationClick(location) {
    if (location.mhmLocationId) return;
    const updatedPosLocations = posLocations.map(it => ({
      ...it,
      selected: it.dbId === location.dbId ? !it.selected : false
    }));
    setSelPosLocation(location);
    setPosLocations(updatedPosLocations);
  }

  function onMhmLocationClick(mhmLocation) {
    if (!selPosLocation || !!mhmLocation.squareLocationId) return;
    if (selPosLocation) {
      mhmLocations.forEach(it => {
        if (it.squareLocationId === selPosLocation.dbId) {
          it.posLocation = "";
          it.squareLocationId = "";
        }
        it.mhmLocationId = "";
        it.mhmLocation = "";
      });

      mhmLocation.posLocation = selPosLocation.locationName || selPosLocation.name;
      mhmLocation.squareLocationId = selPosLocation.dbId;

      selPosLocation.mhmLocationId = mhmLocation.internalId;
      selPosLocation.mhmLocation = mhmLocation.name;
      selPosLocation.selected = false;
    }

    posLocations.forEach(it => {
      it.selected = false;
      if (it.id === selPosLocation.id) {
        it.mhmLocationId = selPosLocation.internalId;
        it.mhmLocation = selPosLocation.name;
      }
    });
    setSelPosLocation(null);
    setMhmLocations([...mhmLocations]);
    setPosLocations([...posLocations]);
  }

  function clearLink(mhmLocation) {
    // Update mhmLocations immutably
    const updatedMhmLocations = mhmLocations.map(loc => {
      if (loc.internalId === mhmLocation.internalId) {
        return {
          ...loc,
          posLocation: "",
          squareLocationId: "",
        };
      }
      return loc;
    });

    // Update posLocations immutably
    const updatedPosLocations = posLocations.map(it => {
      if (it.mhmLocationId === mhmLocation.internalId) {
        return {
          ...it,
          mhmLocationId: "",
          mhmLocation: "",
        };
      }
      return it;
    });

    setMhmLocations(updatedMhmLocations);
    setPosLocations(updatedPosLocations);
  }

  useEffect(() => {
    if (!pendingAuthorization) return;

    const handler = async () => {
      if (document.visibilityState === 'visible') {
        const restaurants = (await posService.getSquareRestaurants()).data;
        const r = restaurants[0];
        setRestaurant(r);
      }
    };
    document.addEventListener("visibilitychange", handler);

    return () => document.removeEventListener("visibilitychange", handler);
  }, [pendingAuthorization]);

  return <div className={"pos-menu"}>
    {importing && <div><Spinner /></div>}
    {!importing && <div>
      {!assignMode && !pendingAuthorization && <header className={"pos-loc-assign"}>
        <div>
          <span className={"toast"}>
            <SquareIcon />
          </span>
          {!!activePosLocation && <h6>
            {activePosLocation.locationName || activePosLocation.name}
          </h6>}
          {!activePosLocation && <h6 className="link" onClick={() => setAssignMode(true)}>
            + Assign Location
          </h6>}
        </div>
        <div className={"sep"}>/</div>
        <div>
          <span className={"mhm"}>mhm</span>
          <h6>{window.data.design.locationName}</h6>
        </div>

        <div className={"cfg"}>
          <Tooltip placement='bottom' title='Assign Locations'>
            <a onClick={() => setAssignMode(true)}><SettingOutlined /></a>
          </Tooltip>
        </div>
      </header>}

      {!activePosLocation && !assignMode &&
      <div class="no-data-message" style="height: 600px;">
        <BarsOutlined/>
        <h3>No data available</h3>
        <p>Assign a POS location to this MustHaveMenus location</p>  
        <button className={"btn primary large"} onClick={() => setAssignMode(true)}>
          Assign Locations
        </button>
      </div>}

      {pendingAuthorization && <div className={"additional-permissions"}>
        <h3>Additional permissions are required from your Square account</h3>
        <div className="btn" onClick={posService.connectToSquare}>
          <span>Click here to authorize new permissions for MustHaveMenus</span>
          <SquareLargeIcon />
        </div>
      </div>}

      {assignMode && <div className={"assign-locs"}>
        <h3>Assign your POS locations to your MustHaveMenus locations</h3>
        <p>First select a POS location, then select a MustHaveMenus location to assign it to</p>

        <div className="lists">
          <ul className={"ls-toast"}>
            <li className={'pos-list-header'}>
              <img src="https://timber.mhmcdn.com/site/marketing/icons/Square.svg" alt="Square logo" />
              <h3>Square</h3>
            </li>
            {posLocations.map(it =>
              <li key={it.dbId} className={`${!!it.mhmLocation ? "disabled" : ""}`}>
                <div onClick={() => onLocationClick(it)} className={`loc ${it.selected ? "sel" : ""}`}>
                  <span>{it.locationName || it.name}</span>
                  {it.mhmLocation && <span className={"s"}>{it.mhmLocation}</span>}
                </div>
              </li>)}
          </ul>
          <ul className={"ls-mhm"}>
            <li className={'pos-list-header'}>
              <img src="https://timber.mhmcdn.com/site/marketing/icons/MHMCircle.svg" alt="mhm logo"/>
              <h3>MustHaveMenus</h3>
            </li>
            <li className={"new-loc"}>
              <a href="/home/account/locations" target="_blank">+ Add Locations</a>
            </li>
            {mhmLocations.map(it =>
              <li key={it.id} className={`${(it.squareLocationId) ? "disabled" : ""}`}>
                <div onClick={() => onMhmLocationClick(it)} className={`loc`}>
                  <span>{it.name}</span>
                  {it.posLocation && <span className={"s"}>{it.posLocation}</span>}
                </div>

                {it.posLocation && <button className={"clear"} onClick={() => clearLink(it)}>Clear</button>}
              </li>)}
          </ul>
        </div>

        <footer>
          <a className="btn primary medium" onClick={doneAssign}>
            Done
          </a>
        </footer>
      </div>}

    </div>}
  </div>
}
